import React from 'react';
import { Link as RouterLink, type LinkProps as RouterLinkProps } from 'react-router-dom';

/**
 * Link Component - Atomic Level
 * 
 * A flexible link component that handles both internal routing
 * (React Router) and external links.
 * 
 * Features:
 * - Automatic internal/external link detection
 * - Multiple variants (default, primary, secondary, muted)
 * - Underline styles
 * - Icon support
 * - Accessible
 */

export type LinkVariant = 'default' | 'primary' | 'secondary' | 'muted' | 'danger';
export type LinkUnderline = 'none' | 'hover' | 'always';

export interface LinkProps extends Omit<RouterLinkProps, 'to'> {
  /** URL to navigate to */
  to?: string;
  /** External URL (opens in new tab) */
  href?: string;
  /** Link variant */
  variant?: LinkVariant;
  /** Underline behavior */
  underline?: LinkUnderline;
  /** Icon to display before the link text */
  leftIcon?: React.ReactNode;
  /** Icon to display after the link text */
  rightIcon?: React.ReactNode;
  /** Whether to show external link icon */
  showExternalIcon?: boolean;
  /** Additional class names */
  className?: string;
  /** Children */
  children: React.ReactNode;
}

const variantClasses: Record<LinkVariant, string> = {
  default: 'text-[#4D4D4D] hover:text-[#1A1A1A]',
  primary: 'text-[#EAB308] hover:text-[#CA8A04]',
  secondary: 'text-[#808080] hover:text-[#666666]',
  muted: 'text-[#999999] hover:text-[#4D4D4D]',
  danger: 'text-red-600 hover:text-red-700',
};

const underlineClasses: Record<LinkUnderline, string> = {
  none: 'no-underline',
  hover: 'no-underline hover:underline',
  always: 'underline',
};

export const Link: React.FC<LinkProps> = ({
  to,
  href,
  variant = 'default',
  underline = 'hover',
  leftIcon,
  rightIcon,
  showExternalIcon = true,
  className = '',
  children,
  ...props
}) => {
  const isExternal = !!href || (to && (to.startsWith('http') || to.startsWith('mailto:') || to.startsWith('tel:')));
  const destination = href || to || '#';
  
  const linkClasses = `
    inline-flex items-center gap-1.5 
    ${variantClasses[variant]} 
    ${underlineClasses[underline]}
    transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-[#EAB308]/20 focus:ring-offset-2 rounded-sm
    ${className}
  `;

  const ExternalIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );

  const linkContent = (
    <>
      {leftIcon && <span className="inline-flex">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="inline-flex">{rightIcon}</span>}
      {isExternal && showExternalIcon && <ExternalIcon />}
    </>
  );

  if (isExternal) {
    return (
      <a
        href={destination}
        className={linkClasses}
        target="_blank"
        rel="noopener noreferrer"
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {linkContent}
      </a>
    );
  }

  return (
    <RouterLink to={destination} className={linkClasses} {...props}>
      {linkContent}
    </RouterLink>
  );
};

export default Link;

